import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cn } from '../lib/utils.js';

const Popover = PopoverPrimitive.Root;
const PopoverTrigger = PopoverPrimitive.Trigger;
const PopoverAnchor = PopoverPrimitive.Anchor;

/**
 * Default gap between a popover's trigger and its content. Exported so
 * `useCalendarPopoverSide` (date-picker-trigger.tsx) measures available space
 * with the same offset the popover renders with — one source of truth.
 */
export const POPOVER_SIDE_OFFSET = 4;

const PopoverContent = React.forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(
  (
    { className, align = 'start', sideOffset = POPOVER_SIDE_OFFSET, onFocusOutside, ...props },
    ref,
  ) => (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        onFocusOutside={(e) => {
          e.preventDefault();
          onFocusOutside?.(e);
        }}
        className={cn(
          'z-50 w-72 rounded-lg border border-border bg-card p-0 shadow-lg outline-none',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  ),
);
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

/**
 * Positioning props that pin a popover below its trigger and disable Radix's
 * collision-based flipping, so the popover's placement never flip-flops while
 * open (CU-86capuj0d). Used by `DateRangePill` (filter-bar variant, always
 * near the top of the page). The form-field calendar pickers instead compute
 * a stable side once per open via `useCalendarPopoverSide` in
 * `date-picker-trigger.tsx`, so a field low on the page opens its calendar
 * upward (CU-86cbe4vuq).
 */
export const CALENDAR_POPOVER_POSITION_PROPS = {
  side: 'bottom',
  avoidCollisions: false,
} as const;

export { Popover, PopoverTrigger, PopoverAnchor, PopoverContent };
